import React, { useCallback, useEffect, useMemo } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { getPromoCampaignBenefitOptions } from '~/lib/benefits/promoCampaignBenefits';
import dayjs from '~/timeInit';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  OutlinedInput,
  Stack,
} from '@mui/material';

import { Input, Select } from '~/components/Common/Forms/fields';

import { PROMO_CAMPAIGN_TIERS } from '~/consts/luxLoyalty';

import { useCreatePromoCampaign } from '~/hooks/apis/promoCampaigns';

import {
  type PromoCampaignBody,
  type PromoCampaignPresenter,
  type PromoCampaignTier,
  luxLoyaltyRegions,
} from '~/services/LuxLoyalty';

interface CreatePromoCampaignDialogProps {
  open: boolean;
  onClose: (result?: { campaign: PromoCampaignPresenter; openAddUsers?: boolean }) => void;
}

interface CreatePromoCampaignFormData {
  name: string;
  region: PromoCampaignBody['region'];
  benefit: PromoCampaignBody['benefit'];
  tiers: Array<PromoCampaignTier>;
  quantity: string;
  startDate: string;
  endDate: string;
}

const formatTierLabel = (tier: PromoCampaignTier) => tier.charAt(0).toUpperCase() + tier.slice(1);

const buildDefaultValues = (benefitCode: PromoCampaignBody['benefit']): CreatePromoCampaignFormData => ({
  name: '',
  region: 'AU',
  benefit: benefitCode,
  tiers: [],
  quantity: '',
  startDate: dayjs().format('YYYY-MM-DD'),
  endDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
});

export default function CreatePromoCampaignDialog({ open, onClose }: CreatePromoCampaignDialogProps) {
  const createMutation = useCreatePromoCampaign();
  const benefitOptions = useMemo(() => getPromoCampaignBenefitOptions(), []);

  const defaultValues = useMemo(
    () => buildDefaultValues(benefitOptions[0]?.code ?? 'airport_transfer'),
    [benefitOptions],
  );

  const { control, handleSubmit, reset, getValues } = useForm<CreatePromoCampaignFormData>({
    defaultValues,
    mode: 'onBlur',
  });

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const buildPayload = useCallback(
    (data: CreatePromoCampaignFormData): PromoCampaignBody => ({
      name: data.name.trim(),
      region: data.region,
      benefit: data.benefit,
      tiers: data.tiers,
      quantity: Number(data.quantity),
      startDate: dayjs.tz(data.startDate, 'Australia/Sydney').startOf('day').toISOString(),
      endDate: dayjs.tz(data.endDate, 'Australia/Sydney').endOf('day').toISOString(),
    }),
    [],
  );

  const onSubmit = useCallback(
    async (data: CreatePromoCampaignFormData, openAddUsers: boolean) => {
      try {
        const response = await createMutation.mutateAsync({ campaign: buildPayload(data) });
        onClose({ campaign: response.result.createdCampaign, openAddUsers });
      } catch {
        // Error handled in mutation hook
      }
    },
    [createMutation, buildPayload, onClose],
  );

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Create Promo Campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Input
            control={control}
            name="name"
            muiLabel="Name"
            required
            fullWidth
            rules={{ required: 'Name is required' }}
          />

          <Select control={control} name="region" muiLabel="Region" rules={{ required: 'Region is required' }}>
            {Object.entries(luxLoyaltyRegions).map(([code, label]) => (
              <MenuItem key={code} value={code}>
                {label}
              </MenuItem>
            ))}
          </Select>

          <Select control={control} name="benefit" muiLabel="Benefit" rules={{ required: 'Benefit is required' }}>
            {benefitOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Controller
            name="tiers"
            control={control}
            rules={{
              validate: (value) => value.length > 0 || 'At least one tier is required',
            }}
            render={({ field, fieldState: { error } }) => (
              <FormControl error={!!error} fullWidth required>
                <InputLabel id="promo-campaign-tiers-label">Tiers</InputLabel>
                <MuiSelect
                  labelId="promo-campaign-tiers-label"
                  multiple
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  input={<OutlinedInput label="Tiers" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={formatTierLabel(value)} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {PROMO_CAMPAIGN_TIERS.map((tier) => (
                    <MenuItem key={tier} value={tier}>
                      <Checkbox checked={field.value.includes(tier)} />
                      <ListItemText primary={formatTierLabel(tier)} />
                    </MenuItem>
                  ))}
                </MuiSelect>
                <FormHelperText>{error?.message}</FormHelperText>
              </FormControl>
            )}
          />

          <Input
            control={control}
            name="quantity"
            muiLabel="Quantity"
            type="number"
            required
            fullWidth
            inputProps={{ min: 1 }}
            rules={{
              required: 'Quantity is required',
              validate: (value: string) => Number(value) > 0 || 'Quantity must be greater than 0',
            }}
          />

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <Input
              control={control}
              name="startDate"
              muiLabel="Start Date"
              type="date"
              required
              fullWidth
              rules={{ required: 'Start date is required' }}
            />
            <Input
              control={control}
              name="endDate"
              muiLabel="End Date"
              type="date"
              required
              fullWidth
              rules={{
                required: 'End date is required',
                validate: (value: string) => {
                  const startDate = getValues('startDate');
                  if (startDate && value && dayjs(value).isBefore(dayjs(startDate))) {
                    return 'End date must be after start date';
                  }
                  return true;
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={() => onClose()} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit((data) => onSubmit(data, false))}
          disabled={createMutation.isPending}
        >
          Create
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit((data) => onSubmit(data, true))}
          disabled={createMutation.isPending}
        >
          Create & Add Users
        </Button>
      </DialogActions>
    </Dialog>
  );
}
